import { Component, ViewChild } from '@angular/core';

import { BaseChartDirective } from 'ng2-charts';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ModalService } from 'src/core/services/modal.service';
import { Chart, ChartConfiguration, ChartDataset, registerables, TimeScale } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { PrimeNG } from 'primeng/config';
import { Table, TableModule } from 'primeng/table';
import { NgShuiModule } from '@shui/angular';
import { ChatService } from 'src/core/services/chat.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

Chart.register(...registerables, TimeScale, zoomPlugin, annotationPlugin);

@Component({
  standalone: true,
  selector: 'app-maintenance-failure-reasons',
  imports: [BaseChartDirective, TableModule, NgShuiModule, TranslocoModule],
  templateUrl: './maintenance-failure-reasons.component.html',
  styleUrl: './maintenance-failure-reasons.component.css'
})
export class MaintenanceFailureReasonsComponent {
  @ViewChild('dt') dt!: Table;
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  
  failureChartData: any = { datasets: [] };
  failureChartOptions: any = {};
  selectedRange: string = '';
  row: any = null;
  tableData: any[] = [];
  filteredTableData: any[] = [];
  originalFailureReasons: any[] = [];
  selectedIndices: number[] = [];
  cols: any[] = [];
  
  // Drag selection variables
  isDragging: boolean = false;
  dragStartX: number = 0;
  dragEndX: number = 0;
  selectionBox: any = null;

  constructor(private modalService: ModalService, private chatService: ChatService, private selectedSystemService: SelectedsystemService, private selectedContentService: SelectedContentService, private translocoService: TranslocoService) { }

  ngOnInit(): void {
    this.cols = [
      { field: 'date_time', header: this.translocoService.translate('maintenanceFailureReasons.table.dateTime') },
      { field: 'reason', header: this.translocoService.translate('maintenanceFailureReasons.table.failureReason') }
    ];

    this.modalService.$modalChanged.subscribe((modal) => {
      if (modal && modal.modal === 'maintenance-failure-reasons' && modal.open === true) {
        this.row = modal.filter[0].value.row;
        this.selectedRange = modal.filter[0].value.selectedRange;
        this.updateTableData();
        this.createFailureChart(this.row);
        this.resizeChart();
      }
    });
  }

   private resizeChart(): void {
    setTimeout(() => {
      if (this.chart?.chart) {
        this.chart.chart.resize();
      }
    }, 100);
  }

  updateTableData() {
    if (this.row && this.selectedRange && this.row['metrics'] && this.row['metrics'][this.selectedRange]) {
      const rawData = this.row['metrics'][this.selectedRange]['failure_reasons'] || [];
      // Sort by date_time
      this.originalFailureReasons = rawData.sort((a: any, b: any) => 
        new Date(a.date_time).getTime() - new Date(b.date_time).getTime()
      );
      
      this.tableData = this.originalFailureReasons.map((item: any, index: number) => ({
        date_time: new Date(item.date_time).toLocaleString(),
        reason: item.reason || 'Unknown',
        originalIndex: index,
        originalDateTime: new Date(item.date_time)
      }));
      
      this.filteredTableData = [...this.tableData];
      this.selectedIndices = [];
    } else {
      this.tableData = [];
      this.filteredTableData = [];
      this.originalFailureReasons = [];
    }
  }

  filterTableBySelectedIndices() {
    if (this.selectedIndices.length > 0) {
      this.filteredTableData = this.tableData.filter(item => 
        this.selectedIndices.includes(item.originalIndex)
      );
    } else {
      this.filteredTableData = [...this.tableData];
    }
  }

  clearFilter() {
    this.selectedIndices = [];
    this.filteredTableData = [...this.tableData];
    this.updateChartColors();
    this.removeSelectionBox();
  }

  updateChartColors() {
    if (this.failureChartData.datasets && this.failureChartData.datasets[0]) {
      const dataset = this.failureChartData.datasets[0];
      const totalBars = dataset.data.length;
      
      if (this.selectedIndices.length > 0) {
        dataset.backgroundColor = Array(totalBars).fill('#E0E0E0').map((color, index) => 
          this.selectedIndices.includes(index) ? '#fbbf28ff' : color
        );
        dataset.borderColor = Array(totalBars).fill('#E0E0E0').map((color, index) => 
          this.selectedIndices.includes(index) ? '#fbbf28ff' : color
        );
      } else {
        dataset.backgroundColor = Array(totalBars).fill('#fbbf28ff');
        dataset.borderColor = Array(totalBars).fill('#fbbf28ff');
      }
      
      this.chart?.update();
    }
  }

  removeSelectionBox() {
    if (this.chart?.chart?.options?.plugins?.annotation?.annotations) {
      delete this.chart.chart.options.plugins.annotation.annotations['selectionBox'];
      this.chart?.update();
    }
  }

  createFailureChart(row: any) {
    if (!row || !this.selectedRange) {
      console.warn('Missing row data or selected range');
      return;
    }

    const failureReasons = row['metrics']?.[this.selectedRange]?.['failure_reasons'] || [];
    
    if (failureReasons.length === 0) {
      this.failureChartData = {
        labels: [],
        datasets: [{
          label: 'No failure data available',
          data: [],
          backgroundColor: '#fbbf28ff'
        }]
      };
      this.failureChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: {
            display: true,
            text: 'No failure reasons found for this period'
          }
        }
      };
      return;
    }
    
    // Sort failure reasons by date_time
    const sortedFailureReasons = [...failureReasons].sort((a: any, b: any) => 
      new Date(a.date_time).getTime() - new Date(b.date_time).getTime()
    );
    
    // Process data for date (X-axis) and time (Y-axis)
    const processedData = sortedFailureReasons.map((item: any) => {
      const dateTime = new Date(item.date_time);
      
      // Extract just the date (without time) for X-axis
      const dateOnly = new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
      
      // Convert time to minutes from midnight for Y-axis (0-1440 minutes in a day)
      const timeInMinutes = dateTime.getHours() * 60 + dateTime.getMinutes();
      
      return {
        x: dateOnly,
        y: timeInMinutes,
        originalDateTime: dateTime,
        reason: item.reason || 'Unknown'
      };
    });

    // Get date range for X-axis
    const dates = processedData.map(item => item.x.getTime());
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    // Add padding to the date range (1 day on each side)
    const paddedMinDate = new Date(minDate.getTime() - 24 * 60 * 60 * 1000);
    const paddedMaxDate = new Date(maxDate.getTime() + 24 * 60 * 60 * 1000);

    this.failureChartData = {
      datasets: [{
        label: 'Failure Events',
        data: processedData,
        backgroundColor: Array(processedData.length).fill('#fbbf28ff'),
        borderColor: Array(processedData.length).fill('#fbbf28ff'),
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        showLine: false, // Don't connect points with lines
        type: 'scatter' // Use scatter plot for better time visualization
      }]
    };

    const componentRef = this;

    this.failureChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'time',
          min: paddedMinDate,
          max: paddedMaxDate,
          time: {
            unit: 'day',
            stepSize: 1,
            displayFormats: {
              day: 'MMM dd, yyyy'
            }
          },
          grid: {
            display: true
          },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: {
              size: 11
            },
            autoSkip: true,
            maxTicksLimit: 10
          },
          title: {
            display: true,
            font: { size: 14, weight: 'bold' },
            text: 'Date'
          }
        },
        y: {
          min: 0,
          max: 1440, // 24 hours * 60 minutes = 1440 minutes
          grid: {
            display: true
          },
          ticks: {
            stepSize: 120, // Every 2 hours (120 minutes)
            callback: function(value: any) {
              // Convert minutes back to time format
              const hours = Math.floor(value / 60);
              const minutes = value % 60;
              return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            },
            font: {
              size: 10
            }
          },
          title: {
            display: true,
            font: { size: 14, weight: 'bold' },
            text: 'Time of Day'
          }
        }
      },
      plugins: {
        tooltip: {
          enabled: true,
          mode: 'point',
          intersect: false,
          callbacks: {
            title: function(context: any) {
              if (context && context.length > 0) {
                const dataIndex = context[0].dataIndex;
                if (dataIndex >= 0 && dataIndex < processedData.length) {
                  return processedData[dataIndex].originalDateTime.toLocaleString();
                }
              }
              return '';
            },
            label: function(context: any) {
              if (context && context.dataIndex >= 0 && context.dataIndex < processedData.length) {
                const dataIndex = context.dataIndex;
                const reason = processedData[dataIndex].reason;
                return `Failure Reason: ${reason}`;
              }
              return '';
            },
            afterLabel: function(context: any) {
              if (context && context.dataIndex >= 0 && context.dataIndex < processedData.length) {
                const dataIndex = context.dataIndex;
                const dateTime = processedData[dataIndex].originalDateTime;
                const timeStr = dateTime.toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                });
                return [`Time: ${timeStr}`];
              }
              return [];
            }
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#fbbf28ff',
          borderWidth: 1,
          cornerRadius: 4,
          displayColors: false
        },
        legend: {
          display: true,
          position: 'top' as const
        },
        title: {
          display: true,
          text: `Failure Events Timeline - ${row.routine_name || 'Maintenance Item'}`,
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        annotation: {
          annotations: {}
        }
      },
      interaction: {
        intersect: false,
        mode: 'point' as const
      },
      animation: {
        onComplete: () => {
          setTimeout(() => {
            if (this.chart?.chart) {
              this.chart.chart.resize();
            }
          }, 100);
        }
      },
      onResize: (chart: any) => {
        const container = chart.canvas.parentNode;
        if (container) {
          chart.canvas.style.maxHeight = container.style.height || '100%';
        }
      },
      onHover: (event: any, activeElements: any) => {
        event.native.target.style.cursor = 'crosshair';
      },
      onClick: (event: any, activeElements: any) => {
        if (!componentRef.isDragging) {
          if (componentRef.selectedIndices.length > 0) {
            // Uncomment if you want to clear on click
            // componentRef.clearFilter();
          }
        }
      }
    };

    // Add drag selection after chart initialization
    setTimeout(() => {
      this.addDragSelection();
    }, 200);
  }

  addDragSelection() {
    const canvas = this.chart?.chart?.canvas;
    if (!canvas) return;

    let isMouseDown = false;
    let startX = 0;
    let currentX = 0;
    let hasDragged = false;

    canvas.addEventListener('mousedown', (e: MouseEvent) => {
      isMouseDown = true;
      this.isDragging = false;
      hasDragged = false;
      const rect = canvas.getBoundingClientRect();

      startX = e.clientX - rect.left; // Adjust for padding/margin
      currentX = startX;
    });

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
      if (!isMouseDown) return;
      
      const rect = canvas.getBoundingClientRect();
      currentX = e.clientX - rect.left; // Adjust for padding/margin
      
      // Only start dragging if moved more than 5 pixels
      if (Math.abs(currentX - startX) > 5) {
        this.isDragging = true;
        hasDragged = true;
        this.drawSelectionBox(startX, currentX);
      }
    });

    canvas.addEventListener('mouseup', (e: MouseEvent) => {
      if (isMouseDown) {
        if (hasDragged && this.isDragging) {
          // Handle drag selection
          this.handleDragEnd(startX, currentX);
        } else {
          // Handle single click - clear selection if exists
          if (this.selectedIndices.length > 0) {
            this.clearFilter();
          }
        }
      }
      isMouseDown = false;
      this.isDragging = false;
      hasDragged = false;
    });
  }

    ngAfterViewInit(): void {
    // Force chart resize after view initialization
      setTimeout(() => {
        if (this.chart?.chart) {
          this.chart.chart.resize();
          //this.cdr.detectChanges();
        }
      }, 200);

      // Also force resize when data changes
      setTimeout(() => {
        if (this.chart?.chart) {
          this.chart.chart.resize();
        }
      }, 500);
    }

  drawSelectionBox(startX: number, currentX: number) {
    const minX = Math.min(startX, currentX);
    const maxX = Math.max(startX, currentX);
    
    if (this.chart?.chart?.options?.plugins?.annotation?.annotations) {
      this.chart.chart.options.plugins.annotation.annotations['selectionBox'] = {
        type: 'box' as const,
        xMin: this.chart.chart.scales['x'].getValueForPixel(minX),
        xMax: this.chart.chart.scales['x'].getValueForPixel(maxX),
        yMin: this.chart.chart.scales['y'].min,
        yMax: this.chart.chart.scales['y'].max,
        backgroundColor: '#ffef631a',
        borderColor: '#1a1717cc',
        borderWidth: 2
      };
      this.chart?.update('none');
    }
  }

  handleDragEnd(startX: number, endX: number) {
    const minX = Math.min(startX, endX);
    const maxX = Math.max(startX, endX);
    
    if (this.chart?.chart) {
      // Convert pixel positions to date values for selection
      const startDate = this.chart.chart.scales['x'].getValueForPixel(minX);
      const endDate = this.chart.chart.scales['x'].getValueForPixel(maxX);
      
      // Clear previous selection and select events within the date range
      this.selectedIndices = [];
      this.originalFailureReasons.forEach((item, index) => {
        const itemDateTime = new Date(item.date_time);
        const itemDateOnly = new Date(itemDateTime.getFullYear(), itemDateTime.getMonth(), itemDateTime.getDate()).getTime();
        
        if (itemDateOnly >= startDate && itemDateOnly <= endDate) {
          this.selectedIndices.push(index);
        }
      });
      
      this.updateChartColors();
      this.filterTableBySelectedIndices();
    }
    
    this.removeSelectionBox();
  }

    askChat(part: string) {

    let prompt = `Maintenance failed for  ${this.selectedContentService.currentSystem.SerialNumber} due to ${part}. How do I resolve this? ?`;

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }
}
